import axios from 'axios';
import store from './store';

const backendBaseURL = (process.env.VUE_APP_DEVELOPMENT_BACKEND_BASE_URL || '').replace(/\/$/, '');

let base = axios.create({
  baseURL: `${backendBaseURL}/api/v1`,
  withCredentials: true,
});

const defaultAdapter = axios.defaults.adapter;
let cryptoKey = null;
let hideNetwork = false;

function hexToBytes(hex) {
  if (!/^[0-9a-f]{64}$/i.test(hex)) {
    throw new Error('VUE_APP_TUNNEL_KEY must be a 32-byte hex value');
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(value) {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function encryptPayload(value) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    new TextEncoder().encode(value)
  );
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  return bytesToBase64(combined);
}

async function decryptPayload(value) {
  const data = base64ToBytes(value);
  const iv = data.slice(0, 12);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, data.slice(12));
  return new Uint8Array(decrypted);
}

function headersToObject(headers) {
  return Object.entries(headers || {}).reduce((result, [key, value]) => {
    if (value !== undefined && value !== null) {
      result[key] = String(value);
    }
    return result;
  }, {});
}

function isBinaryRequest(data) {
  return (
    (typeof FormData !== 'undefined' && data instanceof FormData) ||
    (typeof Blob !== 'undefined' && data instanceof Blob) ||
    data instanceof ArrayBuffer
  );
}

async function initTunnel() {
  const response = await fetch(`${backendBaseURL}/cfg`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Unable to load tunnel configuration');
  }
  const config = await response.json();
  hideNetwork = config.hide === true;
  if (hideNetwork) {
    cryptoKey = await crypto.subtle.importKey(
      'raw',
      hexToBytes(process.env.VUE_APP_TUNNEL_KEY || ''),
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }
}

function responseData(bytes, responseType, contentType) {
  if (responseType === 'blob') {
    return new Blob([bytes], { type: contentType });
  }
  if (responseType === 'arraybuffer') {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  }
  return new TextDecoder().decode(bytes);
}

async function decryptTunnelResponse(response, config) {
  response.config = config;
  const contentType = response.headers['x-tunnel-content-type'] || 'application/json';
  response.headers['content-type'] = contentType;

  if (response.status === 204) {
    response.data = null;
    return response;
  }

  const bytes = await decryptPayload(response.data);
  response.data = responseData(bytes, config.responseType, contentType);
  return response;
}

const tunnelReady = typeof window === 'undefined' ? Promise.resolve() : initTunnel();

base.defaults.adapter = async function (config) {
  await tunnelReady;
  if (!hideNetwork || !cryptoKey || isBinaryRequest(config.data)) {
    return defaultAdapter(config);
  }

  const requestURL = new URL(base.getUri(config), window.location.origin);
  const payload = await encryptPayload(
    JSON.stringify({
      method: config.method.toUpperCase(),
      url: `${requestURL.pathname}${requestURL.search}`,
      headers: headersToObject(config.headers),
      body: config.data === undefined || config.data === null ? '' : String(config.data),
    })
  );
  const tunnelConfig = {
    ...config,
    baseURL: undefined,
    url: new URL('/p', requestURL.origin).toString(),
    method: 'post',
    params: undefined,
    data: payload,
    headers: { 'Content-Type': 'text/plain' },
    responseType: 'text',
  };

  try {
    const response = await defaultAdapter(tunnelConfig);
    return await decryptTunnelResponse(response, config);
  } catch (error) {
    if (error.response) {
      error.response = await decryptTunnelResponse(error.response, config);
    }
    error.config = config;
    throw error;
  }
};

base.interceptors.response.use(
  function (resp) {
    return resp;
  },
  function (error) {
    // Handle request abort errors gracefully (don't treat as errors)
    if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
      // Return a resolved promise with no data instead of rejecting
      return Promise.resolve({ data: null, aborted: true });
    }

    if (error.response && error.response.status === 401) {
      // Clear user state but don't redirect - let navigation guard handle redirect
      store.commit('auth_logout', false);
    }
    return Promise.reject(error);
  }
);

export default base;
